/**
 * CoverBattleCard — "Which cover would make you pick up the book?" Vote
 * once, then see the community split.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useCoverClub } from './context/CoverClubContext';
import CoverArt from './CoverArt';
import { formatCount } from './utils/coverHelpers';

function Side({ book, style, pct, isChosen, hasVoted, onVote, label }) {
  return (
    <button
      type="button"
      onClick={onVote}
      disabled={hasVoted}
      data-testid={`cover-battle-vote-${label}`}
      className={cx('group relative flex-1 overflow-hidden rounded-xl text-left transition-transform duration-300', !hasVoted && 'hover:-translate-y-1')}
    >
      <div className="relative aspect-[2/3] w-full">
        <CoverArt book={book} style={style} className="h-full w-full" />
        {isChosen && (
          <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-coral text-white">
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
        {hasVoted && (
          <div className="absolute inset-x-0 bottom-0 bg-black/55 p-2 backdrop-blur-sm">
            <div className="mb-1 h-1.5 w-full overflow-hidden rounded-full bg-white/25">
              <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-coral" />
            </div>
            <p className="text-xs font-semibold text-white">{pct}%</p>
          </div>
        )}
      </div>
      <p className="mt-2 truncate text-xs font-semibold text-ink">{book.title}</p>
    </button>
  );
}

export default function CoverBattleCard({ battle }) {
  const { getEntry, getBattleResults, voteBattle } = useCoverClub();
  const entryA = getEntry(battle.a);
  const entryB = getEntry(battle.b);
  if (!entryA || !entryB) return null;

  const results = getBattleResults(battle);
  const hasVoted = Boolean(results.myVote);

  const handleVote = async (choice) => {
    if (hasVoted) return;
    try {
      await voteBattle(battle.id, choice);
      toast('Vote counted — thanks for weighing in!');
    } catch (error) {
      toast(error.message || 'We could not record your vote.');
    }
  };

  return (
    <div className="rounded-2xl border border-line bg-card p-4 shadow-soft transition-shadow duration-300 hover:shadow-card" data-testid="cover-club-battle">
      <p className="font-display text-base font-semibold text-ink">{battle.prompt}</p>
      <p className="mt-0.5 text-xs text-subtle">{battle.context}</p>

      <div className="relative mt-4 flex gap-3">
        <Side book={entryA.book} style={entryA.style} pct={results.pctA} isChosen={results.myVote === 'a'} hasVoted={hasVoted} onVote={() => handleVote('a')} label="a" />

        <div className="pointer-events-none absolute left-1/2 top-[38%] z-10 -translate-x-1/2 -translate-y-1/2">
          <span className="grid h-9 w-9 place-items-center rounded-full border-2 border-cream bg-ink font-display text-[11px] font-bold text-cream shadow-lift">
            VS
          </span>
        </div>

        <Side book={entryB.book} style={entryB.style} pct={results.pctB} isChosen={results.myVote === 'b'} hasVoted={hasVoted} onVote={() => handleVote('b')} label="b" />
      </div>

      {hasVoted && <p className="mt-3 text-center text-[11px] text-subtle">{formatCount(results.total)} votes so far</p>}
    </div>
  );
}
